import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { usePublicPosition } from "@/api/hooks/public";
import { useSubmitApplication } from "@/api/hooks/mutations";
import { ApiError } from "@/api/client";
import { Button, buttonClass } from "@/components/ui/Button";
import { Field, fieldInputClass } from "@/components/ui/Field";
import { ChipInput } from "@/components/ui/ChipInput";
import { FileUploader } from "@/components/ui/FileUploader";
import { fmtSalary, humanize } from "@/lib/format";

const applySchema = z.object({
  full_name: z.string().min(2, "Tell us your full name"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().min(4, "Phone is required"),
  location: z.string().optional().or(z.literal("")),
  linkedin_url: z.string().optional().or(z.literal("")),
  telegram_url: z.string().optional().or(z.literal("")),
  github_url: z.string().optional().or(z.literal("")),
  cover_letter: z.string().min(10, "A short cover letter helps"),
  years_experience: z.string().min(1, "Pick one"),
  current_role: z.string().optional().or(z.literal("")),
  expected_rate: z.string().min(1, "Required"),
  engagement_pref: z.string().optional().or(z.literal("")),
  earliest_start: z.string().optional().or(z.literal("")),
  notice_period: z.string().optional().or(z.literal("")),
  additional_info: z.string().optional().or(z.literal("")),
  consent: z.literal(true, { message: "Consent is required" }),
  website: z.string().max(0).optional().or(z.literal("")),
});

type ApplyInput = z.infer<typeof applySchema>;

export function ApplyPage() {
  const { id } = useParams();
  const positionId = parseInt(id ?? "", 10);
  const valid = !isNaN(positionId);
  const position = usePublicPosition(valid ? positionId : null);
  const submit = useSubmitApplication();
  const [skills, setSkills] = useState<string[]>([]);
  const [cv, setCv] = useState<File | null>(null);
  const [cvInvalid, setCvInvalid] = useState(false);
  const [skillsInvalid, setSkillsInvalid] = useState(false);
  const [topError, setTopError] = useState("");
  const [done, setDone] = useState(false);

  const form = useForm<ApplyInput>({
    resolver: zodResolver(applySchema),
    defaultValues: { years_experience: "6-9 years" },
  });

  // Pre-fill skills from the position once it loads.
  useEffect(() => {
    if (position.data?.skills?.length && skills.length === 0) {
      setSkills(position.data.skills);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position.data]);

  if (!valid) return <NotValid />;
  if (position.isLoading) {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-12 text-text-muted">
        Loading job details…
      </div>
    );
  }
  if (position.error || !position.data) return <NotValid />;

  const p = position.data;

  async function onSubmit(values: ApplyInput) {
    setTopError("");
    if (values.website) {
      // Honeypot tripped — silently succeed.
      setDone(true);
      return;
    }
    if (!skills.length) {
      setSkillsInvalid(true);
      setTopError("Please list at least one skill.");
      return;
    }
    if (!cv) {
      setCvInvalid(true);
      setTopError("Please attach your CV.");
      return;
    }
    setSkillsInvalid(false);
    setCvInvalid(false);

    const fd = new FormData();
    fd.set("full_name", values.full_name);
    fd.set("email", values.email);
    fd.set("phone", values.phone);
    fd.set("cover_letter", values.cover_letter);
    fd.set("years_experience", values.years_experience);
    fd.set("expected_rate", values.expected_rate);
    fd.set("currency", p.currency || "USD");
    fd.set("position_id", String(positionId));
    fd.set("consent", values.consent ? "true" : "false");
    if (values.location) fd.set("location", values.location);
    if (values.linkedin_url) fd.set("linkedin_url", values.linkedin_url);
    if (values.telegram_url) fd.set("telegram_url", values.telegram_url);
    if (values.github_url) fd.set("github_url", values.github_url);
    if (values.current_role) fd.set("current_role", values.current_role);
    if (values.engagement_pref) fd.set("engagement_pref", values.engagement_pref);
    if (values.earliest_start) fd.set("earliest_start", values.earliest_start);
    if (values.notice_period) fd.set("notice_period", values.notice_period);
    if (values.additional_info) fd.set("additional_info", values.additional_info);
    fd.set("skills", skills.join(", "));
    fd.set("website", "");
    fd.set("cv", cv);

    try {
      await submit.mutateAsync(fd);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 415)
          setTopError("That file type isn't supported. PDF, DOC or DOCX please.");
        else if (err.status === 413)
          setTopError("File is too large — keep your CV under 10 MB.");
        else if (err.status === 429)
          setTopError("Too many submissions — please try again in a minute.");
        else setTopError(err.message || "Could not submit your application.");
      } else {
        setTopError("Network error. Please try again.");
      }
    }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-[1100px] px-6 py-16">
        <div className="rounded-2xl border border-border bg-surface p-10 text-center">
          <h1 className="text-2xl font-bold">Application submitted</h1>
          <p className="mt-2 text-text-muted">
            Thanks — we'll be in touch within 2 business days.
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <Link to="/jobs" className={buttonClass()}>
              Browse more jobs
            </Link>
            <Link to="/" className={buttonClass({ variant: "secondary" })}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const initial = ((p.company_name || p.title || "•").trim()[0] ?? "•").toUpperCase();

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-12">
      <div className="mb-3 text-sm text-text-muted">
        <Link to="/" className="hover:text-text">
          Home
        </Link>{" "}
        ›{" "}
        <Link to="/jobs" className="hover:text-text">
          Find a job
        </Link>{" "}
        › Apply
      </div>

      {/* Job summary */}
      <div className="mb-6 flex items-start gap-4.5 rounded-2xl border border-border bg-surface p-5">
        <div className="grid size-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-warning to-danger text-xl font-bold text-white">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold">{p.title}</h2>
          <p className="text-sm text-text-muted">
            {p.company_name}
            {p.location ? ` · ${p.location}` : ""}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-3.5 text-[13px] text-text-muted">
            {p.engagement_type && (
              <span>
                {humanize(p.engagement_type, "service")}
                {p.schedule ? ` · ${humanize(p.schedule, "engagement")}` : ""}
              </span>
            )}
            <span className="font-semibold text-text">
              {fmtSalary(p.salary_min, p.salary_max, p.currency)}
            </span>
          </div>
        </div>
        <Link to="/jobs" className={buttonClass({ variant: "ghost", size: "sm" })}>
          ← Back to jobs
        </Link>
      </div>

      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="grid items-start gap-6 md:grid-cols-[1fr_320px]"
      >
        {/* Honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...form.register("website")}
          className="absolute -left-[9999px]"
          aria-hidden
        />

        <div className="flex flex-col gap-5">
          <Card title="About you" hint="We'll use these to get in touch.">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Full name" required error={form.formState.errors.full_name?.message}>
                <input
                  type="text"
                  placeholder="e.g. Jordan Smith"
                  {...form.register("full_name")}
                  className={fieldInputClass(!!form.formState.errors.full_name)}
                />
              </Field>
              <Field label="Email" required error={form.formState.errors.email?.message}>
                <input
                  type="email"
                  placeholder="jordan@example.com"
                  {...form.register("email")}
                  className={fieldInputClass(!!form.formState.errors.email)}
                />
              </Field>
              <Field label="Phone" required error={form.formState.errors.phone?.message}>
                <input
                  type="tel"
                  placeholder="+1 555 0100"
                  {...form.register("phone")}
                  className={fieldInputClass(!!form.formState.errors.phone)}
                />
              </Field>
              <Field label="Location">
                <input
                  type="text"
                  placeholder="e.g. Lisbon, Portugal (UTC+0)"
                  {...form.register("location")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="LinkedIn">
                <input
                  type="url"
                  placeholder="linkedin.com/in/…"
                  {...form.register("linkedin_url")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Telegram">
                <input
                  type="text"
                  placeholder="@username or t.me/username"
                  {...form.register("telegram_url")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="GitHub">
                <input
                  type="url"
                  placeholder="github.com/…"
                  {...form.register("github_url")}
                  className={fieldInputClass()}
                />
              </Field>
            </div>
          </Card>

          <Card title="CV / Resume *" hint="PDF, DOC or DOCX up to 10 MB.">
            <FileUploader
              value={cv}
              onChange={(f) => {
                setCv(f);
                if (f) setCvInvalid(false);
              }}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              invalid={cvInvalid}
            />
          </Card>

          <Card
            title="Cover letter & experience"
            hint="A few short paragraphs about why this role fits."
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label="Cover letter"
                required
                error={form.formState.errors.cover_letter?.message}
                className="sm:col-span-2"
              >
                <textarea
                  placeholder="Why this role? What relevant work have you shipped?"
                  {...form.register("cover_letter")}
                  className={`${fieldInputClass(!!form.formState.errors.cover_letter)} min-h-[160px] resize-y`}
                />
              </Field>
              <Field label="Years of experience" required>
                <select
                  {...form.register("years_experience")}
                  className={fieldInputClass()}
                >
                  <option value="0–2 years">0–2 years</option>
                  <option value="3–5 years">3–5 years</option>
                  <option value="6-9 years">6–9 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </Field>
              <Field label="Current role">
                <input
                  type="text"
                  placeholder="e.g. Senior Backend Engineer @ Stripe"
                  {...form.register("current_role")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Top skills" required className="sm:col-span-2">
                <ChipInput
                  ariaLabel="Top skills"
                  value={skills}
                  onChange={(v) => {
                    setSkills(v);
                    if (v.length) setSkillsInvalid(false);
                  }}
                  placeholder="Add and press Enter…"
                  invalid={skillsInvalid}
                />
              </Field>
            </div>
          </Card>

          <Card title="Rate & availability">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field
                label={`Expected monthly rate (${p.currency || "USD"})`}
                required
                error={form.formState.errors.expected_rate?.message}
                hint={`Posted: ${fmtSalary(p.salary_min, p.salary_max, p.currency)}`}
              >
                <input
                  type="text"
                  placeholder="e.g. $8,000"
                  {...form.register("expected_rate")}
                  className={fieldInputClass(!!form.formState.errors.expected_rate)}
                />
              </Field>
              <Field label="Engagement preference">
                <select
                  {...form.register("engagement_pref")}
                  className={fieldInputClass()}
                >
                  <option value="">—</option>
                  <option value="full_time">Full-time</option>
                  <option value="part_time">Part-time</option>
                  <option value="project_based">Project-based</option>
                </select>
              </Field>
              <Field label="Earliest start date">
                <input
                  type="text"
                  placeholder="e.g. May 1, 2026"
                  {...form.register("earliest_start")}
                  className={fieldInputClass()}
                />
              </Field>
              <Field label="Notice period">
                <select {...form.register("notice_period")} className={fieldInputClass()}>
                  <option value="">—</option>
                  <option value="immediate">Available immediately</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="1 month">1 month</option>
                  <option value="2+ months">2+ months</option>
                </select>
              </Field>
              <Field label="Anything else?" className="sm:col-span-2">
                <textarea
                  placeholder="Time-zone, visa, time-off plans — anything that helps."
                  {...form.register("additional_info")}
                  className={`${fieldInputClass()} min-h-[100px] resize-y`}
                />
              </Field>
            </div>

            <label
              className={`mt-4 flex items-start gap-2.5 rounded-md p-3.5 ${
                form.formState.errors.consent
                  ? "bg-danger-soft outline outline-1 outline-danger"
                  : "bg-surface-alt"
              }`}
            >
              <input
                type="checkbox"
                {...form.register("consent")}
                className="mt-0.5 size-4 accent-primary"
              />
              <span className="text-[13px] leading-relaxed text-text-muted">
                I agree that NgonTech may store and process my application data to
                match me with relevant roles. I can withdraw at any time.
              </span>
            </label>

            {topError && (
              <div className="mt-4 rounded-md bg-danger-soft px-3 py-2 text-sm text-[#b91c1c]">
                {topError}
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-3 border-t border-border pt-4">
              <Link to="/jobs" className={buttonClass({ variant: "secondary" })}>
                Cancel
              </Link>
              <Button type="submit" loading={form.formState.isSubmitting}>
                Submit application
              </Button>
            </div>
          </Card>
        </div>

        <aside className="sticky top-[88px] flex flex-col gap-4">
          <Card title="About this role">
            <p className="text-[13px] leading-relaxed text-text-muted">
              {p.description}
            </p>
            <div className="my-3.5 h-px bg-border" />
            <div className="flex flex-wrap gap-1.5">
              {(p.skills ?? []).map((s) => (
                <span
                  key={s}
                  className="rounded-full bg-surface-alt px-2.5 py-0.5 text-xs font-medium"
                >
                  {s}
                </span>
              ))}
            </div>
          </Card>
          <Card title="What happens next">
            <ol className="flex flex-col gap-3 text-[13px]">
              <Step n={1}>We review your application within 2 business days.</Step>
              <Step n={2}>If matched, we set up a short intro call.</Step>
              <Step n={3}>You meet the merchant team directly to make it official.</Step>
            </ol>
          </Card>
        </aside>
      </form>
    </div>
  );
}

function Card({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-6">
      <h2 className="mb-1 text-base font-semibold">{title}</h2>
      {hint && <p className="mb-5 text-[13px] text-text-muted">{hint}</p>}
      {children}
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="grid size-[22px] shrink-0 place-items-center rounded-full bg-primary-soft text-[11px] font-bold text-primary-hover">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

function NotValid() {
  return (
    <div className="mx-auto max-w-[600px] px-6 py-20 text-center">
      <h1 className="text-2xl font-bold">Job not found</h1>
      <p className="mt-2 text-text-muted">
        This role may have been closed or removed.
      </p>
      <div className="mt-5">
        <Link to="/jobs" className={buttonClass()}>
          Back to job board
        </Link>
      </div>
    </div>
  );
}
